import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { PageHeader } from './_shared';
import {
  AS_OF_LABEL,
  CASH_ACCOUNTS,
  EXPENSE_BREAKDOWN,
  MONTHLY_SALES,
  OVERVIEW,
  PERIOD_LABEL,
  SUPPLIER_INVOICES,
  peso,
  pesoShort,
} from './sample-data';

/** Earliest due date among the open invoices — the one to chase first. */
const OLDEST_UNPAID = [...SUPPLIER_INVOICES].sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0]!;

const TOOLTIP_STYLE = {
  background: 'hsl(var(--popover))',
  color: 'hsl(var(--popover-foreground))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  boxShadow: 'var(--shadow-elevated)',
  fontSize: 12,
} as const;

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
      {children}
    </p>
  );
}

/** Money-in / money-out figure with a proportional bar beneath it. */
function BarFigure({
  label,
  amount,
  max,
  tone,
}: {
  label: string;
  amount: number;
  max: number;
  tone: 'credit' | 'debit';
}) {
  const pct = max > 0 ? Math.max((amount / max) * 100, 2) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="font-mono text-sm tabular-nums">{peso(amount)}</span>
      </div>
      <div className="mt-1.5 h-2 w-full rounded-full bg-muted">
        <div
          className={`h-2 rounded-full ${tone === 'credit' ? 'bg-credit' : 'bg-debit'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function AccountingOverviewPage() {
  const salesUp = OVERVIEW.netSalesDeltaPct >= 0;
  const plMax = Math.max(OVERVIEW.totalRevenue, OVERVIEW.cogs + OVERVIEW.operatingExpenses);
  const expenseMax = Math.max(...EXPENSE_BREAKDOWN.map((e) => e.amount));
  const apOverduePct = (OVERVIEW.apOverdue / OVERVIEW.apTotal) * 100;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Accounting"
        subtitle={`Period ${PERIOD_LABEL} · balances as of ${AS_OF_LABEL}`}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1fr_340px]">
        {/* ---------------------------------------------------------------- */}
        {/* Payables — outstanding split overdue / not yet due               */}
        {/* ---------------------------------------------------------------- */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Payables</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-2xl font-semibold">{pesoShort(OVERVIEW.apTotal)}</p>
            <Eyebrow>Outstanding to suppliers</Eyebrow>

            <div className="mt-4 flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
              <div
                className="h-2.5 rounded-l-full bg-warning"
                style={{ width: `${apOverduePct}%` }}
              />
              <div className="h-2.5 flex-1 rounded-r-full bg-muted" />
            </div>

            <div className="mt-3 flex justify-between gap-4">
              <div>
                <p className="font-mono text-base tabular-nums text-warning">
                  {peso(OVERVIEW.apOverdue)}
                </p>
                <Eyebrow>Overdue</Eyebrow>
              </div>
              <div className="text-right">
                <p className="font-mono text-base tabular-nums">{peso(OVERVIEW.apCurrent)}</p>
                <Eyebrow>Not due yet</Eyebrow>
              </div>
            </div>

            <dl className="mt-5 space-y-2 border-t pt-4 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Open supplier invoices</dt>
                <dd className="font-mono tabular-nums">{SUPPLIER_INVOICES.length}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Awaiting three-way match</dt>
                <dd className="font-mono tabular-nums">
                  {SUPPLIER_INVOICES.filter((i) => i.match === 'Variance').length}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="shrink-0 text-muted-foreground">Oldest unpaid</dt>
                <dd className="truncate text-right">{OLDEST_UNPAID.supplier}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Operating expenses — magnitude, so one hue, sorted, labelled     */}
        {/* ---------------------------------------------------------------- */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Operating expenses</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-2xl font-semibold">{pesoShort(OVERVIEW.operatingExpenses)}</p>
            <Eyebrow>{PERIOD_LABEL}</Eyebrow>

            <ul className="mt-4 space-y-2">
              {EXPENSE_BREAKDOWN.map((e) => (
                <li key={e.label}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-xs text-muted-foreground" title={e.label}>
                      {e.label}
                    </span>
                    <span className="shrink-0 font-mono text-xs tabular-nums">
                      {peso(e.amount)}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full bg-primary"
                      style={{ width: `${Math.max((e.amount / expenseMax) * 100, 1.5)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Cash & money in transit — the tall right-hand column             */}
        {/* ---------------------------------------------------------------- */}
        <Card className="xl:row-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cash &amp; receivables</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y">
              {CASH_ACCOUNTS.map((a) => (
                <li key={a.code} className="px-6 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium">{a.name}</span>
                    <span className="font-mono text-sm tabular-nums">{peso(a.balance)}</span>
                  </div>
                  <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {a.code}
                    {a.note ? ` · ${a.note}` : ''}
                  </p>
                </li>
              ))}
            </ul>
            <div className="border-t px-6 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-semibold">Total</span>
                <span className="font-mono text-sm font-semibold tabular-nums">
                  {peso(CASH_ACCOUNTS.reduce((s, a) => s + a.balance, 0))}
                </span>
              </div>
            </div>
            <div className="border-t px-6 py-4">
              <Eyebrow>Posting status</Eyebrow>
              <p className="mt-1 text-sm">
                <span className="font-semibold tabular-nums">
                  {OVERVIEW.entriesPosted.toLocaleString('en-PH')}
                </span>{' '}
                <span className="text-muted-foreground">journal entries posted</span>
              </p>
              <p className="mt-1 text-sm">
                <span className="font-semibold tabular-nums text-credit">
                  {OVERVIEW.unpostedDocuments}
                </span>{' '}
                <span className="text-muted-foreground">documents awaiting posting</span>
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Sales, collections, commissions, payouts, receiving and write-offs post
                automatically. Last posted {OVERVIEW.lastPostedAt}.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Profit & loss                                                    */}
        {/* ---------------------------------------------------------------- */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Profit &amp; loss</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <div>
              <p className="text-3xl font-semibold text-credit">{pesoShort(OVERVIEW.netIncome)}</p>
              <Eyebrow>Net income for {PERIOD_LABEL}</Eyebrow>
            </div>
            <BarFigure
              label="Total revenue"
              amount={OVERVIEW.totalRevenue}
              max={plMax}
              tone="credit"
            />
            <BarFigure label="Cost of goods sold" amount={OVERVIEW.cogs} max={plMax} tone="debit" />
            <BarFigure
              label="Operating expenses"
              amount={OVERVIEW.operatingExpenses}
              max={plMax}
              tone="debit"
            />
          </CardContent>
        </Card>

        {/* ---------------------------------------------------------------- */}
        {/* Sales trend — single series, so one hue and no legend box        */}
        {/* ---------------------------------------------------------------- */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Net sales</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-semibold">{pesoShort(OVERVIEW.netSales)}</p>
              <span
                className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${
                  salesUp ? 'text-credit' : 'text-debit'
                }`}
              >
                {salesUp ? (
                  <ArrowUpRight className="h-3 w-3" />
                ) : (
                  <ArrowDownRight className="h-3 w-3" />
                )}
                {Math.abs(OVERVIEW.netSalesDeltaPct).toFixed(1)}%
              </span>
            </div>
            <Eyebrow>{PERIOD_LABEL} vs. prior month</Eyebrow>

            <div className="mt-3 h-[190px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={MONTHLY_SALES} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    tickMargin={6}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  {/* Explicit clean ticks: a generic formatter rounded 1.5M to
                      "2M", so the axis was labelling values it wasn't at. */}
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={11}
                    width={44}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, 6_000_000]}
                    ticks={[0, 2_000_000, 4_000_000, 6_000_000]}
                    tickFormatter={(v: number) => `${v / 1_000_000}M`}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(v) => [peso(Number(v)), 'Net sales'] as [string, string]}
                  />
                  {/* Animation off: the line is the content, not an effect —
                      and it must be present the instant the page is shown. */}
                  <Line
                    type="monotone"
                    dataKey="netSales"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    isAnimationActive={false}
                    dot={{
                      r: 3,
                      stroke: 'hsl(var(--card))',
                      strokeWidth: 2,
                      fill: 'hsl(var(--primary))',
                    }}
                    activeDot={{
                      r: 5,
                      stroke: 'hsl(var(--card))',
                      strokeWidth: 2,
                      fill: 'hsl(var(--primary))',
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
